import { Fragment, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  AlertTriangle,
  BadgeCheck,
  BookOpen,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  Coins,
  CreditCard,
  Eye,
  Banknote,
  FileText,
  Hourglass,
  Inbox,
  Landmark,
  ListChecks,
  PieChart,
  Receipt,
  RotateCcw,
  Search,
  Undo2,
  Users,
  Wallet,
  X,
  XCircle,
  type LucideIcon,
} from "lucide-react";
import { ConfirmDialog, type ConfirmRequest } from "../components/ConfirmDialog";
import { ErrorState } from "../components/PageState";
import { Pager, paginate } from "../components/Pager";
import { financialService, type MyApprovalItem } from "../services/financialService";
import { staffService } from "../services/staffService";
import { useAIStatusQuery } from "../hooks/useAIStatus";
import {
  activeApprovalFilterCount,
  APPROVAL_PAGE_SIZES,
  APPROVAL_STATUS_DOTS,
  approvalStatusCounts,
  approvalStatusTabs,
  EMPTY_APPROVAL_FILTERS,
  filterApprovals,
  isFigure,
  type ApprovalFilters,
} from "./approvalFilters";

const financialError = (failure: unknown) => {
  const payload = (failure as { response?: { data?: unknown } })?.response?.data;
  if (payload && typeof payload === "object") {
    return Object.entries(payload)
      .map(([field, value]) => `${field}: ${Array.isArray(value) ? value.join(" ") : String(value)}`)
      .join(" ");
  }
  return "That action could not be completed.";
};

const DOCUMENT_LABELS: Record<string, string> = {
  "financial.account": "Chart of accounts",
  "financial.accountrole": "Account roles",
  "financial.journalentry": "Journal entry",
  "financial.vendorbill": "Vendor bill",
  "financial.budget": "Budget",
  "financial.creditnote": "Credit note",
  "financial.payrollperiod": "Payroll period",
  "financial.payrollsettlement": "Payroll payment",
  "financial.salaryadvance": "Salary advance",
  "financial.salaryadvancedisbursement": "Advance payout",
  "financial.refundrequest": "Refund request",
  "financial.grant": "Grant",
  "financial.accountingperiodreopenrequest": "Period reopen request",
  "financial.bankreconciliationsignoff": "Bank reconciliation",
  "staff.salarychangerequest": "Salary change",
};

const TAB_LABELS: Record<string, string> = {
  accounts: "Chart of Accounts",
  journals: "Journals",
  payables: "Payables",
  budgets: "Budgets",
  credits: "Credit Notes",
  payroll: "Payroll",
  refunds: "Refunds",
  grants: "Grants",
  periods: "Accounting Periods",
  banking: "Banking",
  salary: "Staff → Salary changes",
};

// What each document is called mid-sentence. DOCUMENT_LABELS names a screen
// ("Chart of accounts"); this names the thing being acted on, so the dialogs
// can say "this account request" rather than "this chart of accounts".
const DOCUMENT_NOUNS: Record<string, string> = {
  "financial.account": "account request",
  "financial.accountrole": "account role change",
  "financial.journalentry": "journal entry",
  "financial.vendorbill": "vendor bill",
  "financial.budget": "budget",
  "financial.creditnote": "credit note",
  "financial.payrollperiod": "payroll run",
  "financial.refundrequest": "refund request",
  "financial.grant": "grant",
  "financial.accountingperiodreopenrequest": "request to reopen the period",
  "financial.bankreconciliationsignoff": "bank reconciliation",
};

/** What the system asks before it acts on a document, in the school's own
 *  words rather than the browser's "localhost:3000 says" box.
 *
 *  Verify, return and reject look alike in the queue but do very different
 *  things -- one hands the document on, one hands it back, one ends it -- so
 *  each says which of those it is, who ends up holding the document, and
 *  whether it can still be corrected afterwards. */
export const approvalPrompt = (
  action: "verify" | "return" | "reject",
  item: { title: string; document_type: string; initiated_by?: string; id?: number },
): Omit<ConfirmRequest, "onConfirm"> & { body: string } => {
  const noun = DOCUMENT_NOUNS[item.document_type] ?? "document";
  const preparer = item.initiated_by || "whoever prepared it";
  // The assistant reads this one document, as the queue serves it to this
  // reviewer, and drafts the note for the step being taken. Rough notes typed
  // first are what it builds on.
  const ai = (draftLabel: string, draftHint: string) =>
    item.id === undefined
      ? undefined
      : {
          context: {
            key: "financial.approval_note" as const,
            params: { document_type: item.document_type, id: item.id, purpose: action },
          },
          draftLabel,
          draftHint,
        };
  if (action === "verify") {
    return {
      title: `Verify ${item.title}`,
      body: `You are confirming that you have checked this ${noun} and it is correct. It then waits on the approver, who has the final say — verifying does not approve it.`,
      confirmLabel: "Verify",
      tone: "primary",
      field: {
        label: "Comment for the approver",
        placeholder: "Anything the approver should know before they decide",
        helper: "Kept on the document's history alongside your name.",
        ai: ai("Draft from the document", "Reads this document to draft a comment. You edit before it is saved."),
      },
    };
  }
  if (action === "return") {
    return {
      title: `Return ${item.title} for changes`,
      body: item.document_type === "financial.bankreconciliationsignoff"
        ? `This ${noun} goes back to ${preparer}, who puts it up again once the statement or the books are put right. Nothing is signed meanwhile.`
        : `This goes back to ${preparer}, and only they can act on it from there: they can correct it and send it round again, or delete it if the school turns out not to need it. Nothing is lost.`,
      confirmLabel: "Return for changes",
      tone: "warning",
      field: {
        label: "What needs to change?",
        placeholder: "e.g. The name is misspelt — it should read Transport expense.",
        required: true,
        helper: `${preparer} sees this word for word, so say what to fix.`,
        ai: ai("Draft what to change", "Jot rough notes first and it turns them into a clear request."),
      },
    };
  }
  return {
    title: `Reject ${item.title}`,
    body: `Rejecting closes this ${noun} for good. It stays on the record with your reason, but it cannot be corrected and sent round again — if it only needs a change, return it instead.`,
    confirmLabel: "Reject",
    tone: "danger",
    field: {
      label: "Why is this being rejected?",
      placeholder: "e.g. This duplicates an account the school already has.",
      required: true,
      helper: "Recorded permanently on the document's history.",
      ai: ai("Draft the reason", "Give your reason in a few words first; it will not invent one."),
    },
  };
};

const DOCUMENT_ICONS: Record<string, LucideIcon> = {
  "financial.account": BookOpen,
  "financial.accountrole": BookOpen,
  "financial.journalentry": FileText,
  "financial.vendorbill": Receipt,
  "financial.budget": PieChart,
  "financial.creditnote": CreditCard,
  "financial.payrollperiod": Users,
  "financial.payrollsettlement": Banknote,
  "financial.salaryadvance": Wallet,
  "financial.salaryadvancedisbursement": Banknote,
  "financial.refundrequest": RotateCcw,
  "financial.grant": Coins,
  "financial.accountingperiodreopenrequest": CalendarClock,
  "financial.bankreconciliationsignoff": Landmark,
  "staff.salarychangerequest": Wallet,
};

const formatDate = (value: string) => {
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" });
};

interface DocumentActions {
  verify?: (id: number, comment: string) => Promise<unknown>;
  return?: (id: number, reason: string) => Promise<unknown>;
  reject: (id: number, reason: string) => Promise<unknown>;
  finalizeLabel?: string;
  finalize?: (id: number) => Promise<unknown>;
}

const ACTION_MAP: Record<string, DocumentActions> = {
  "financial.account": {
    verify: (id, comment) => financialService.verifyAccount({ id, comment }),
    return: (id, reason) => financialService.returnAccount({ id, reason }),
    reject: (id, reason) => financialService.rejectAccount({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveAccount(id),
  },
  // Which account a role posts to: approving it redirects live postings.
  "financial.accountrole": {
    verify: (id, comment) => financialService.verifyAccountRoleChange({ id, comment }),
    return: (id, reason) => financialService.returnAccountRoleChange({ id, reason }),
    reject: (id, reason) => financialService.rejectAccountRoleChange({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveAccountRoleChange(id),
  },
  "financial.journalentry": {
    verify: (id, comment) => financialService.verifyJournalEntry({ id, comment }),
    return: (id, reason) => financialService.returnJournalEntry({ id, reason }),
    reject: (id, reason) => financialService.rejectJournalEntry({ id, reason }),
    finalizeLabel: "Approve & post",
    finalize: (id) => financialService.postJournalEntry(id),
  },
  "financial.vendorbill": {
    verify: (id, comment) => financialService.verifyVendorBill({ id, comment }),
    return: (id, reason) => financialService.returnVendorBill({ id, reason }),
    reject: (id, reason) => financialService.rejectVendorBill({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveVendorBill(id),
  },
  "financial.budget": {
    verify: (id, comment) => financialService.verifyBudget({ id, comment }),
    return: (id, reason) => financialService.returnBudget({ id, reason }),
    reject: (id, reason) => financialService.rejectBudget({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveBudget(id),
  },
  "financial.creditnote": {
    verify: (id, comment) => financialService.verifyCreditNote({ id, comment }),
    return: (id, reason) => financialService.returnCreditNote({ id, reason }),
    reject: (id, reason) => financialService.rejectCreditNote({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveCreditNote(id),
  },
  "financial.grant": {
    verify: (id, comment) => financialService.verifyGrant({ id, comment }),
    return: (id, reason) => financialService.returnGrant({ id, reason }),
    reject: (id, reason) => financialService.rejectGrant({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveGrant(id),
  },
  "financial.payrollperiod": {
    verify: (id, comment) => financialService.verifyPayrollPeriod({ id, comment }),
    return: (id, reason) => financialService.returnPayrollPeriod({ id, reason }),
    reject: (id, reason) => financialService.rejectPayrollPeriod({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approvePayrollPeriod({ id }),
  },
  "financial.payrollsettlement": {
    verify: (id, comment) => financialService.verifyPayrollSettlement({ id, comment }),
    return: (id, reason) => financialService.returnPayrollSettlement({ id, reason }),
    reject: (id, reason) => financialService.rejectPayrollSettlement({ id, reason }),
    // This is the click that actually takes money out of the bank, so it says so.
    finalizeLabel: "Approve & pay",
    finalize: (id) => financialService.approvePayrollSettlement({ id }),
  },
  // Two people decide an advance: the staff member asks, finance approves.
  "financial.salaryadvance": {
    reject: (id, reason) => financialService.rejectSalaryAdvance({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveSalaryAdvance({ id }),
  },
  // Paying it out is a payment like any other, so it is reviewed and approved.
  "financial.salaryadvancedisbursement": {
    verify: (id, comment) => financialService.verifyAdvancePayout({ id, comment }),
    return: (id, reason) => financialService.returnAdvancePayout({ id, reason }),
    reject: (id, reason) => financialService.rejectAdvancePayout({ id, reason }),
    finalizeLabel: "Approve & pay",
    finalize: (id) => financialService.approveAdvancePayout({ id }),
  },
  "financial.refundrequest": {
    reject: (id, reason) => financialService.rejectRefundRequest({ id, notes: reason }),
    finalizeLabel: "Approve",
    finalize: (id) => financialService.approveRefundRequest({ id }),
  },
  "staff.salarychangerequest": {
    return: (id, reason) => staffService.returnSalaryChange({ id, reason }),
    reject: (id, reason) => staffService.rejectSalaryChange({ id, reason }),
    finalizeLabel: "Approve",
    finalize: (id) => staffService.approveSalaryChange(id),
  },
  // Signing it means nothing dated on or before its date is entered for that bank again.
  "financial.bankreconciliationsignoff": {
    verify: (id, comment) => financialService.verifyBankSignOff({ id, comment }),
    return: (id, reason) => financialService.returnBankSignOff({ id, reason }),
    reject: (id, reason) => financialService.rejectBankSignOff({ id, reason }),
    finalizeLabel: "Sign off",
    finalize: (id) => financialService.approveBankSignOff(id),
  },
  "financial.accountingperiodreopenrequest": {
    verify: (id, comment) => financialService.verifyAccountingPeriodReopen({ id, comment }),
    return: (id, reason) => financialService.returnAccountingPeriodReopen({ id, reason }),
    reject: (id, reason) => financialService.rejectAccountingPeriodReopen({ id, reason }),
    finalizeLabel: "Approve reopening",
    finalize: (id) => financialService.approveAccountingPeriodReopen(id),
  },
};

const ROW_BUTTON =
  "inline-flex items-center gap-1 rounded px-2 py-0.5 text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50";

/** Workflow status as a coloured dot and label, compact enough for a table row. */
function ApprovalStatus({ status }: { status: string }) {
  return (
    <span className="inline-flex items-center gap-1.5 text-xs font-medium capitalize text-gray-700">
      <span className={`h-1.5 w-1.5 rounded-full ${APPROVAL_STATUS_DOTS[status] || "bg-gray-400"}`} />
      {status.replace(/_/g, " ")}
    </span>
  );
}

/** The document behind a queue row, fetched only when someone opens it.
 *
 * The queue deliberately shows one line per document; this is what turns
 * "approve BILL-000004" into a decision somebody can actually make. */
function ApprovalDetailPanel({ item }: { item: MyApprovalItem }) {
  const detail = useQuery({
    queryKey: ["approval-detail", item.document_type, item.id, item.workflow_status],
    queryFn: () => financialService.getApprovalDetail({ documentType: item.document_type, id: item.id }),
  });

  if (detail.isLoading) {
    return <div className="px-3 py-4 text-sm text-gray-500">Loading the document…</div>;
  }
  if (detail.isError || !detail.data) {
    return (
      <div className="px-3 py-4 text-sm text-red-700">
        This document could not be opened. Refresh the queue — it may already have been acted on.
      </div>
    );
  }

  const { summary, lines, history } = detail.data;
  // A column is a figure column when every filled cell in it is a number.
  const figureColumns = lines
    ? lines.columns.map((_, column) =>
        lines.rows.some((row) => row[column]?.trim()) &&
        lines.rows.every((row) => !row[column]?.trim() || row[column] === "-" || isFigure(row[column])),
      )
    : [];
  return (
    <div className="space-y-4 px-3 py-4">
      <dl className="grid gap-x-6 gap-y-3 sm:grid-cols-2 lg:grid-cols-4">
        {summary.map((row) => (
          <div key={row.label}>
            <dt className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">{row.label}</dt>
            <dd className="mt-0.5 text-sm text-gray-900">{row.value || "—"}</dd>
          </div>
        ))}
      </dl>

      {lines && lines.rows.length > 0 && (
        <div className="max-w-4xl space-y-1.5">
          <div className="overflow-x-auto rounded-md border border-gray-200 bg-white">
            <table className="sheet">
              <thead className="static">
                <tr>
                  <th className="sheet-rownum">#</th>
                  {lines.columns.map((column, index) => (
                    <th key={column} className={figureColumns[index] ? "text-right" : undefined}>{column}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {lines.rows.map((row, index) => (
                  <tr key={index}>
                    <td className="sheet-rownum">{index + 1}</td>
                    {row.map((cell, cellIndex) => (
                      <td
                        key={cellIndex}
                        className={figureColumns[cellIndex] ? "text-right tabular-nums" : "text-gray-900"}
                      >
                        {cell && cell !== "-" ? cell : <span className="text-gray-300">—</span>}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {lines.total && <p className="text-xs font-semibold text-gray-900">{lines.total}</p>}
        </div>
      )}

      {history.length > 0 && (
        <div>
          <h4 className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">History</h4>
          <ol className="mt-1.5 space-y-1 border-l-2 border-gray-200 pl-3">
            {history.map((event, index) => (
              <li key={index} className="text-xs text-gray-700">
                <span className="font-semibold capitalize text-gray-900">{event.action.replace(/_/g, " ")}</span>
                {" by "}{event.actor}
                <span className="text-gray-500">{" · "}{formatDate(event.at)}</span>
                {event.comment && <span className="text-gray-500"> — “{event.comment}”</span>}
              </li>
            ))}
          </ol>
        </div>
      )}
    </div>
  );
}

export default function MyApprovals() {
  const queryClient = useQueryClient();
  const [error, setError] = useState("");
  const [openItem, setOpenItem] = useState<string | null>(null);
  const [dialog, setDialog] = useState<ConfirmRequest | null>(null);
  const [filters, setFilters] = useState(EMPTY_APPROVAL_FILTERS);
  const [pageRequested, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(APPROVAL_PAGE_SIZES[1]);
  // Asked up front, so the writing assistant is ready when a dialog opens.
  useAIStatusQuery();

  const approvals = useQuery({
    queryKey: ["my-approvals"],
    queryFn: financialService.getMyApprovals,
    // This screen exists to be watched: someone sits on it waiting for work to
    // arrive, so it refreshes itself rather than making them reload. Paused
    // while the tab is in the background, where nobody is looking.
    refetchInterval: 20_000,
    refetchIntervalInBackground: false,
  });

  const act = useMutation({
    mutationFn: (fn: () => Promise<unknown>) => fn(),
    onSuccess: () => {
      setError("");
      return queryClient.invalidateQueries({ queryKey: ["my-approvals"] });
    },
    onError: (failure) => setError(financialError(failure)),
  });

  const handleVerify = (item: MyApprovalItem) => {
    const config = ACTION_MAP[item.document_type];
    // The server says whose turn it is; an approver is never offered a review.
    if (!config?.verify || !item.actions.includes("verify")) return;
    setDialog({
      ...approvalPrompt("verify", item),
      onConfirm: (comment) => act.mutate(() => config.verify!(item.id, comment)),
    });
  };

  const handleReturn = (item: MyApprovalItem) => {
    const config = ACTION_MAP[item.document_type];
    if (!config?.return) return;
    setDialog({
      ...approvalPrompt("return", item),
      onConfirm: (reason) => act.mutate(() => config.return!(item.id, reason)),
    });
  };

  const handleReject = (item: MyApprovalItem) => {
    const config = ACTION_MAP[item.document_type];
    if (!config) return;
    setDialog({
      ...approvalPrompt("reject", item),
      onConfirm: (reason) => act.mutate(() => config.reject(item.id, reason)),
    });
  };

  const handleFinalize = (item: MyApprovalItem) => {
    const config = ACTION_MAP[item.document_type];
    if (!item.actions.includes("approve") || !config?.finalize) return;
    act.mutate(() => config.finalize!(item.id));
  };

  const items = approvals.data?.items ?? [];
  const submittedCount = items.filter((item) => item.workflow_status === "submitted").length;
  const totalAmount = items.reduce((sum, item) => sum + (item.amount ? Number(item.amount) : 0), 0);

  const updateFilters = (changes: Partial<ApprovalFilters>) => {
    setFilters((current) => ({ ...current, ...changes }));
    setPage(1);
  };
  const statusCounts = approvalStatusCounts(
    filterApprovals(items, filters, DOCUMENT_LABELS, { ignoreStatus: true }),
  );
  const documentTypes = [...new Set(items.map((item) => item.document_type))];
  const visible = filterApprovals(items, filters, DOCUMENT_LABELS);
  const activeFilters = activeApprovalFilterCount(filters);
  // Clamped, so approving the last row on a page does not strand the reader on an empty one.
  const { rows: paged, first: firstRow, page, pageCount } = paginate(visible, pageRequested, pageSize);
  const columnCount = 9;

  return (
    <div className="space-y-6">
      <div className="relative overflow-hidden rounded-xl bg-gradient-to-r from-primary-800 via-primary-700 to-primary-600 p-6 text-white shadow-md sm:p-8">
        <div className="pointer-events-none absolute -right-12 -top-12 h-44 w-44 rounded-full bg-white/10" />
        <div className="pointer-events-none absolute -bottom-16 right-28 h-36 w-36 rounded-full bg-white/5" />
        <div className="relative flex flex-wrap items-center justify-between gap-4">
          <div className="flex items-center gap-4">
            <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-white/15 ring-1 ring-white/25">
              <ClipboardCheck className="h-6 w-6" />
            </div>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">My Approvals</h1>
              <p className="mt-1 max-w-2xl text-sm text-primary-100">
                Everything waiting on you, in one place — accounts, journals, payables, budgets,
                credit notes, payroll, refunds, grants and salary changes — instead of a tab each.
              </p>
            </div>
          </div>
          {!approvals.isLoading && (
            <div className="flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 text-sm font-semibold ring-1 ring-white/25">
              <ListChecks className="h-4 w-4" />
              {items.length} pending
            </div>
          )}
        </div>
      </div>

      {error && (
        <div className="flex items-start gap-3 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {approvals.isError && (
        <ErrorState
          title="The queue could not be loaded"
          message="Check your connection and try again."
          retry={() => approvals.refetch()}
        />
      )}

      {!approvals.isLoading && !approvals.isError && !items.length && (
        <div className="card flex flex-col items-center gap-3 px-6 py-16 text-center">
          <div className="flex h-16 w-16 items-center justify-center rounded-full bg-emerald-50 text-emerald-500">
            <Inbox className="h-8 w-8" />
          </div>
          <h2 className="text-lg font-semibold text-gray-900">All caught up</h2>
          <p className="max-w-sm text-sm text-gray-500">
            Nothing is waiting on you right now. New documents submitted for your review will appear
            here automatically.
          </p>
        </div>
      )}

      {!approvals.isLoading && !!items.length && (
        <div className="grid gap-4 sm:grid-cols-3">
          <div className="card flex items-center gap-4 p-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-primary-50 text-primary-600">
              <ListChecks className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{items.length}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">In your queue</div>
            </div>
          </div>
          <div className="card flex items-center gap-4 p-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-amber-50 text-amber-600">
              <Hourglass className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">{submittedCount}</div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Awaiting verification</div>
            </div>
          </div>
          <div className="card flex items-center gap-4 p-4">
            <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600">
              <Wallet className="h-5 w-5" />
            </div>
            <div>
              <div className="text-2xl font-bold text-gray-900">
                {totalAmount ? `UGX ${totalAmount.toLocaleString()}` : "—"}
              </div>
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">Total value</div>
            </div>
          </div>
        </div>
      )}

      {(approvals.isLoading || !!items.length) && (
        <div className="card">
          <div className="card-header pb-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
              <ListChecks className="h-5 w-5" /> Approval queue
            </h2>
            <p className="text-sm text-gray-500">
              Oldest first. Click a row to read the document before you decide.
            </p>
          </div>
          <div className="card-content space-y-4">
            <div className="filter-panel">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div role="group" aria-label="Workflow status" className="segmented">
                  {approvalStatusTabs(items).map(([value, label]) => (
                    <button
                      key={label}
                      type="button"
                      aria-pressed={filters.status === value}
                      onClick={() => updateFilters({ status: value })}
                      className="segmented-option capitalize"
                    >
                      {label}
                      <span className="ml-1.5 tabular-nums opacity-70">{statusCounts[value] ?? 0}</span>
                    </button>
                  ))}
                </div>
                {activeFilters > 0 && (
                  <button
                    type="button"
                    onClick={() => updateFilters(EMPTY_APPROVAL_FILTERS)}
                    className="inline-flex items-center gap-1 text-xs font-medium text-primary-700 hover:underline"
                  >
                    <X className="h-3.5 w-3.5" /> Clear {activeFilters} {activeFilters === 1 ? "filter" : "filters"}
                  </button>
                )}
              </div>
              <div className="grid gap-3 sm:grid-cols-[minmax(16rem,2fr)_minmax(0,1fr)]">
                <label className="filter-field">
                  Search
                  <div className="relative">
                    <Search className="pointer-events-none absolute left-2.5 top-3 h-4 w-4 text-gray-400" />
                    <input
                      type="search"
                      value={filters.search}
                      onChange={(event) => updateFilters({ search: event.target.value })}
                      className="input-compact pl-8"
                      placeholder="Title, preparer or kind of document"
                    />
                  </div>
                </label>
                <label className="filter-field">
                  Document
                  <select
                    value={filters.documentType}
                    onChange={(event) => updateFilters({ documentType: event.target.value })}
                    className="input-compact"
                  >
                    <option value="">All documents</option>
                    {documentTypes.map((type) => (
                      <option key={type} value={type}>
                        {DOCUMENT_LABELS[type] ?? type}
                      </option>
                    ))}
                  </select>
                </label>
              </div>
            </div>

            {approvals.isLoading ? (
              <div className="space-y-2">
                {Array.from({ length: 5 }).map((_, index) => (
                  <div key={index} className="h-7 animate-pulse rounded bg-gray-100" />
                ))}
              </div>
            ) : !visible.length ? (
              <div className="rounded-lg border border-dashed border-gray-300 py-10 text-center text-sm text-gray-500">
                Nothing in your queue matches these filters.
              </div>
            ) : (
              <div className="space-y-3">
                <div className="sheet-wrap">
                  <table className="sheet">
                    <thead>
                      <tr>
                        <th className="sheet-rownum">#</th>
                        <th>Document</th>
                        <th>Title</th>
                        <th>Section</th>
                        <th>Prepared by</th>
                        <th>Submitted</th>
                        <th>Status</th>
                        <th className="text-right">Amount (UGX)</th>
                        <th className="text-right">Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {paged.map((item, index) => {
                        const config = ACTION_MAP[item.document_type];
                        const DocumentIcon = DOCUMENT_ICONS[item.document_type] ?? FileText;
                        const key = `${item.document_type}-${item.id}`;
                        const isOpen = openItem === key;
                        const toggle = () => setOpenItem(isOpen ? null : key);
                        return (
                          <Fragment key={key}>
                            <tr
                              className={`cursor-pointer ${isOpen ? "bg-primary-50/60" : ""}`}
                              aria-expanded={isOpen}
                              onClick={toggle}
                            >
                              <td className="sheet-rownum">{firstRow + index}</td>
                              <td className="text-gray-700">
                                <span className="inline-flex items-center gap-1.5">
                                  <DocumentIcon className="h-3.5 w-3.5 text-primary-600" />
                                  {DOCUMENT_LABELS[item.document_type] ?? item.document_type}
                                </span>
                              </td>
                              <td className="max-w-[18rem] truncate font-medium text-gray-900" title={item.title}>
                                {item.title}
                              </td>
                              <td className="text-gray-600">{TAB_LABELS[item.tab] ?? item.tab}</td>
                              <td className="text-gray-600">
                                {item.initiated_by || <span className="text-gray-300">—</span>}
                              </td>
                              <td className="tabular-nums text-xs text-gray-600">
                                {item.created_at ? formatDate(item.created_at) : <span className="text-gray-300">—</span>}
                              </td>
                              <td>
                                <ApprovalStatus status={item.workflow_status} />
                              </td>
                              <td className="text-right tabular-nums">
                                {item.amount ? Number(item.amount).toLocaleString() : <span className="text-gray-300">—</span>}
                              </td>
                              <td className="text-right" onClick={(event) => event.stopPropagation()}>
                                <div className="inline-flex items-center gap-1">
                                  <button
                                    type="button"
                                    className={`${ROW_BUTTON} text-primary-700 hover:bg-primary-50`}
                                    aria-expanded={isOpen}
                                    onClick={toggle}
                                  >
                                    <Eye className="h-3.5 w-3.5" />
                                    {isOpen ? "Hide" : "View"}
                                  </button>
                                  {config?.verify && item.actions.includes("verify") && (
                                    <button
                                      type="button"
                                      className={`${ROW_BUTTON} bg-primary-50 text-primary-700 ring-1 ring-inset ring-primary-200 hover:bg-primary-100`}
                                      disabled={act.isLoading}
                                      onClick={() => handleVerify(item)}
                                    >
                                      <BadgeCheck className="h-3.5 w-3.5" />
                                      Verify
                                    </button>
                                  )}
                                  {config?.return && item.actions.includes("return") && (
                                    <button
                                      type="button"
                                      className={`${ROW_BUTTON} text-amber-700 hover:bg-amber-50`}
                                      disabled={act.isLoading}
                                      onClick={() => handleReturn(item)}
                                    >
                                      <Undo2 className="h-3.5 w-3.5" />
                                      Return
                                    </button>
                                  )}
                                  {item.actions.includes("approve") && (config?.finalize ? (
                                    <button
                                      type="button"
                                      className={`${ROW_BUTTON} bg-primary-600 text-white hover:bg-primary-700`}
                                      disabled={act.isLoading}
                                      onClick={() => handleFinalize(item)}
                                    >
                                      <CheckCircle2 className="h-3.5 w-3.5" />
                                      {config.finalizeLabel ?? "Approve"}
                                    </button>
                                  ) : (
                                    <span className="px-2 text-xs text-gray-500">
                                      Finish on the {TAB_LABELS[item.tab] ?? item.tab} tab
                                    </span>
                                  ))}
                                  {item.actions.includes("reject") && (
                                    <button
                                      type="button"
                                      className={`${ROW_BUTTON} text-red-700 hover:bg-red-50`}
                                      disabled={act.isLoading}
                                      onClick={() => handleReject(item)}
                                    >
                                      <XCircle className="h-3.5 w-3.5" />
                                      Reject
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                            {isOpen && (
                              <tr className="bg-secondary-50/60 hover:bg-secondary-50/60">
                                <td colSpan={columnCount} className="whitespace-normal p-0">
                                  <ApprovalDetailPanel item={item} />
                                </td>
                              </tr>
                            )}
                          </Fragment>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <Pager
                  first={firstRow}
                  shown={paged.length}
                  total={visible.length}
                  noun={visible.length === 1 ? "document" : "documents"}
                  page={page}
                  pageCount={pageCount}
                  onPage={setPage}
                  pageSize={pageSize}
                  pageSizes={APPROVAL_PAGE_SIZES}
                  onPageSize={(size) => {
                    setPageSize(size);
                    setPage(1);
                  }}
                />
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmDialog request={dialog} onClose={() => setDialog(null)} busy={act.isLoading} />
    </div>
  );
}
